import { useTranslation } from 'react-i18next';
import { Controller } from 'react-hook-form';
import FormField from '../../../core/components/FormField.tsx';
import StringField from '../../../core/components/Fields/StringField.tsx';
import TextField from '../../../core/components/Fields/TextField.tsx';
import SelectField from '../../../core/components/Fields/SelectField.tsx';
import Switch from '../../../core/components/Switch.tsx';
import Alert from '../../../core/components/Alert.tsx';
import { LookupDefinitionSelectField } from '../../../document/components/MasterDataConfig.tsx';
import SliderField from '../../../document/components/SliderField.tsx';
import React, { useEffect } from 'react';
import {
    CALLER_VERIFICATION_ATTEMPTS_MAX,
    CALLER_VERIFICATION_ATTEMPTS_MIN,
    CALLER_VERIFICATION_FIELD_EXTRA_DATA,
    CALLER_VERIFICATION_FIELDS,
    CALLER_VERIFICATION_DEFAULT_QUESTIONS,
    CALLER_VERIFICATION_FIELDS_WITHOUT_DEFAULT_LABEL,
    MAX_CALL_DURATION_MAX,
    MAX_CALL_DURATION_MIN,
    MAX_CALL_DURATION_STEP,
    VOICE_MODEL_CONVERSATIONAL,
    VOICE_MODEL_FAST,
    VOICE_SPEED_MAX,
    VOICE_SPEED_MIN,
    VOICE_SPEED_STEP,
} from '../constants.ts';
import { GREETING_VARIABLES } from '../promptContext.ts';
import { VoiceAgentPromptEditor } from './VoiceAgentPromptEditor.tsx';

const formatCallDuration = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const rest = Math.round(seconds % 60);
    return `${minutes}:${String(rest).padStart(2, '0')} min`;
};

const VoiceAgentVoiceSettingsFormFields = ({ form }) => {
    const { t } = useTranslation('voice_agents');
    const formErrors = form.formState.errors;
    const voiceModelOptions = [
        { value: VOICE_MODEL_CONVERSATIONAL, label: t('form.voiceModelConversational') },
        { value: VOICE_MODEL_FAST, label: t('form.voiceModelFast') },
    ];
    const speedAdjustable = form.watch('voiceModel') === VOICE_MODEL_FAST;
    const hasTransferNumber = Boolean(form.watch('transferPhoneNumber')?.trim());

    // forwarding on consent denial dials the transfer number — without one there is nothing to forward to
    useEffect(() => {
        if (!hasTransferNumber && form.getValues('forwardOnConsentDenial')) {
            form.setValue('forwardOnConsentDenial', false, { shouldDirty: true });
        }
    }, [hasTransferNumber]);

    // callers are identified only through the caller identification lookup — both gates need it
    const hasIdentificationLookup = Boolean(form.watch('callerIdentificationLookupDefinitionId'));
    // dirty only when the user picked the lookup in this form, not when a saved agent is loaded into it
    const lookupPickedByUser = Boolean(form.formState.dirtyFields.callerIdentificationLookupDefinitionId);
    useEffect(() => {
        if (hasIdentificationLookup) {
            // caller ID is spoofable: identifying callers turns the second factor on unless the user turns it off
            if (lookupPickedByUser && !form.getValues('requireCallerVerification')) {
                form.setValue('requireCallerVerification', true, { shouldDirty: true });
            }
            return;
        }
        ['requireIdentifiedCaller', 'requireCallerVerification'].forEach((name) => {
            if (form.getValues(name)) form.setValue(name, false, { shouldDirty: true });
        });
    }, [hasIdentificationLookup]);

    const callerVerificationFieldOptions = CALLER_VERIFICATION_FIELDS.map((value) => ({
        value,
        label: t(`form.callerVerificationFieldOptions.${value}`),
    }));
    const verifiesCallers = form.watch('requireCallerVerification');
    const verificationField = form.watch('callerVerificationField');
    const questionRequired = CALLER_VERIFICATION_FIELDS_WITHOUT_DEFAULT_LABEL.has(verificationField);
    // what the agent asks when the field stays empty — only the customer number has a standard question
    const defaultQuestion = questionRequired
        ? t('form.callerVerificationQuestionPlaceholder')
        : CALLER_VERIFICATION_DEFAULT_QUESTIONS[form.watch('language')] || CALLER_VERIFICATION_DEFAULT_QUESTIONS.de;

    return (
        <div className="flex flex-col gap-4">
            <Controller
                name="introSpeech"
                control={form.control}
                render={({ field }) => (
                    <FormField
                        label={t('form.introSpeech')}
                        error={formErrors.introSpeech}
                        helpText={t('form.introSpeechHelpText')}
                    >
                        <TextField
                            {...field}
                            inputClassName="h-20 resize-y"
                            placeholder={t('form.introSpeechPlaceholder')}
                        />
                    </FormField>
                )}
            />

            <Controller
                name="introSpeechPersonalized"
                control={form.control}
                render={({ field }) => (
                    <FormField
                        label={t('form.introSpeechPersonalized')}
                        error={formErrors.introSpeechPersonalized}
                        helpText={t('form.introSpeechPersonalizedHelpText')}
                    >
                        <VoiceAgentPromptEditor
                            value={field.value || ''}
                            onValueChange={field.onChange}
                            variables={GREETING_VARIABLES}
                            placeholder={t('form.introSpeechPersonalizedPlaceholder')}
                            minHeightClass="min-h-[5rem]"
                        />
                    </FormField>
                )}
            />

            <Controller
                name="consentQuestion"
                control={form.control}
                rules={{ required: t('form.consentQuestionRequiredError') as string }}
                render={({ field }) => (
                    <FormField
                        label={t('form.consentQuestion')}
                        required={true}
                        error={formErrors.consentQuestion}
                        helpText={t('form.consentQuestionHelpText')}
                    >
                        <TextField
                            {...field}
                            inputClassName="h-16 resize-y"
                            placeholder={t('form.consentQuestionPlaceholder')}
                        />
                    </FormField>
                )}
            />

            <Controller
                name="forwardOnConsentDenial"
                control={form.control}
                render={({ field }) => (
                    <div className={hasTransferNumber ? 'flex flex-col gap-2' : 'flex flex-col gap-2 opacity-50'}>
                        <span className="text-sm font-medium text-content-strong">
                            {t('form.forwardOnConsentDenial')}
                        </span>
                        <span className="text-xs text-content-medium">
                            {hasTransferNumber
                                ? t('form.forwardOnConsentDenialHelpText')
                                : t('form.forwardOnConsentDenialMissingTransferNumberHelpText')}
                        </span>
                        <Switch
                            value={hasTransferNumber && field.value}
                            onValueChange={field.onChange}
                            disabled={!hasTransferNumber}
                        />
                    </div>
                )}
            />

            <div className="flex gap-4">
                <Controller
                    name="phoneNumber"
                    control={form.control}
                    render={({ field }) => (
                        <FormField
                            label={t('form.phoneNumber')}
                            error={formErrors.phoneNumber}
                            helpText={t('form.phoneNumberHelpText')}
                            className="flex-1"
                        >
                            <StringField {...field} placeholder="+49..." />
                        </FormField>
                    )}
                />
                <Controller
                    name="transferPhoneNumber"
                    control={form.control}
                    render={({ field }) => (
                        <FormField
                            label={t('form.transferPhoneNumber')}
                            error={formErrors.transferPhoneNumber}
                            helpText={t('form.transferPhoneNumberHelpText')}
                            className="flex-1"
                        >
                            <StringField {...field} placeholder="+49..." />
                        </FormField>
                    )}
                />
            </div>

            <Controller
                name="callerIdentificationLookupDefinitionId"
                control={form.control}
                render={({ field }) => (
                    <FormField
                        label={t('form.callerIdentificationLookupDefinitionId')}
                        error={formErrors.callerIdentificationLookupDefinitionId}
                        helpText={t('form.callerIdentificationLookupDefinitionIdHelpText')}
                    >
                        <LookupDefinitionSelectField field={field} lookupType="CONTACT_LOOKUP" />
                    </FormField>
                )}
            />

            <Controller
                name="requireIdentifiedCaller"
                control={form.control}
                render={({ field }) => (
                    <div className="flex flex-col gap-2">
                        <span className="text-sm font-medium text-content-strong">
                            {t('form.requireIdentifiedCaller')}
                        </span>
                        <span className="text-xs text-content-medium">
                            {hasIdentificationLookup
                                ? t('form.requireIdentifiedCallerHelpText')
                                : t('form.callerIdentificationLookupMissingHelpText')}
                        </span>
                        <Switch
                            value={hasIdentificationLookup && field.value}
                            onValueChange={field.onChange}
                            disabled={!hasIdentificationLookup}
                        />
                    </div>
                )}
            />

            <Controller
                name="callerRejectionMessage"
                control={form.control}
                rules={{
                    validate: (value) =>
                        !form.watch('requireIdentifiedCaller') ||
                        value?.trim().length > 0 ||
                        (t('form.callerRejectionMessageRequiredError') as string),
                }}
                render={({ field }) => {
                    const isEnabled = form.watch('requireIdentifiedCaller');
                    return (
                        <FormField
                            label={t('form.callerRejectionMessage')}
                            required={isEnabled}
                            error={formErrors.callerRejectionMessage}
                            helpText={t('form.callerRejectionMessageHelpText')}
                            className={!isEnabled ? 'opacity-50' : undefined}
                        >
                            <TextField
                                {...field}
                                inputClassName="h-16 resize-y"
                                placeholder={t('form.callerRejectionMessagePlaceholder')}
                                disabled={!isEnabled}
                            />
                        </FormField>
                    );
                }}
            />

            <Controller
                name="requireCallerVerification"
                control={form.control}
                render={({ field }) => (
                    <div className="flex flex-col gap-2">
                        <span className="text-sm font-medium text-content-strong">
                            {t('form.requireCallerVerification')}
                        </span>
                        <span className="text-xs text-content-medium">
                            {hasIdentificationLookup
                                ? t('form.requireCallerVerificationHelpText')
                                : t('form.callerIdentificationLookupMissingHelpText')}
                        </span>
                        <Switch
                            value={hasIdentificationLookup && field.value}
                            onValueChange={field.onChange}
                            disabled={!hasIdentificationLookup}
                        />
                        {hasIdentificationLookup && !field.value && (
                            <Alert severity="warning">{t('form.requireCallerVerificationOffWarning')}</Alert>
                        )}
                    </div>
                )}
            />

            {verifiesCallers && (
                <>
                    <div className="flex gap-4">
                        <Controller
                            name="callerVerificationField"
                            control={form.control}
                            render={({ field }) => (
                                <FormField
                                    label={t('form.callerVerificationField')}
                                    error={formErrors.callerVerificationField}
                                    helpText={t('form.callerVerificationFieldHelpText')}
                                    className="flex-1"
                                >
                                    <SelectField
                                        {...field}
                                        options={callerVerificationFieldOptions}
                                        renderOption={(option) => option.label}
                                    />
                                </FormField>
                            )}
                        />
                        {verificationField === CALLER_VERIFICATION_FIELD_EXTRA_DATA && (
                            <Controller
                                name="callerVerificationExtraDataKey"
                                control={form.control}
                                rules={{
                                    validate: (value) =>
                                        value?.trim().length > 0 ||
                                        (t('form.callerVerificationExtraDataKeyRequiredError') as string),
                                }}
                                render={({ field }) => (
                                    <FormField
                                        label={t('form.callerVerificationExtraDataKey')}
                                        required
                                        error={formErrors.callerVerificationExtraDataKey}
                                        helpText={t('form.callerVerificationExtraDataKeyHelpText')}
                                        className="flex-1"
                                    >
                                        <StringField {...field} placeholder="pin" />
                                    </FormField>
                                )}
                            />
                        )}
                    </div>

                    <div className="flex gap-4">
                        <Controller
                            name="callerVerificationQuestion"
                            control={form.control}
                            rules={{
                                validate: (value) =>
                                    !CALLER_VERIFICATION_FIELDS_WITHOUT_DEFAULT_LABEL.has(
                                        form.getValues('callerVerificationField')
                                    ) ||
                                    value?.trim().length > 0 ||
                                    (t('form.callerVerificationQuestionRequiredError') as string),
                            }}
                            render={({ field }) => (
                                <FormField
                                    label={t('form.callerVerificationQuestion')}
                                    required={questionRequired}
                                    error={formErrors.callerVerificationQuestion}
                                    helpText={
                                        questionRequired
                                            ? t('form.callerVerificationQuestionRequiredHelpText')
                                            : t('form.callerVerificationQuestionHelpText')
                                    }
                                    className="flex-[3]"
                                >
                                    <StringField {...field} placeholder={defaultQuestion} />
                                </FormField>
                            )}
                        />
                        <Controller
                            name="callerVerificationMaxAttempts"
                            control={form.control}
                            rules={{
                                validate: (value) => {
                                    const attempts = Number(value);
                                    return (
                                        (Number.isInteger(attempts) &&
                                            attempts >= CALLER_VERIFICATION_ATTEMPTS_MIN &&
                                            attempts <= CALLER_VERIFICATION_ATTEMPTS_MAX) ||
                                        (t('form.callerVerificationMaxAttemptsInvalidError', {
                                            min: CALLER_VERIFICATION_ATTEMPTS_MIN,
                                            max: CALLER_VERIFICATION_ATTEMPTS_MAX,
                                        }) as string)
                                    );
                                },
                            }}
                            render={({ field }) => (
                                <FormField
                                    label={t('form.callerVerificationMaxAttempts')}
                                    error={formErrors.callerVerificationMaxAttempts}
                                    helpText={t('form.callerVerificationMaxAttemptsHelpText')}
                                    className="flex-1"
                                >
                                    <StringField {...field} placeholder="3" />
                                </FormField>
                            )}
                        />
                    </div>

                    <Controller
                        name="callerVerificationFailedMessage"
                        control={form.control}
                        render={({ field }) => (
                            <FormField
                                label={t('form.callerVerificationFailedMessage')}
                                error={formErrors.callerVerificationFailedMessage}
                                helpText={t('form.callerVerificationFailedMessageHelpText')}
                            >
                                <TextField
                                    {...field}
                                    inputClassName="h-16 resize-y"
                                    placeholder={t('form.callerVerificationFailedMessagePlaceholder')}
                                />
                            </FormField>
                        )}
                    />
                </>
            )}

            <Controller
                name="backgroundVoiceDetection"
                control={form.control}
                render={({ field }) => (
                    <div className="flex flex-col gap-2">
                        <span className="text-sm font-medium text-content-strong">
                            {t('form.backgroundVoiceDetection')}
                        </span>
                        <span className="text-xs text-content-medium">
                            {t('form.backgroundVoiceDetectionHelpText')}
                        </span>
                        <Switch value={field.value} onValueChange={field.onChange} />
                    </div>
                )}
            />

            <div className="flex flex-col gap-4 border-t border-line-weak pt-4">
                <div className="flex flex-col gap-1">
                    <span className="text-sm font-medium text-content-strong">
                        {t('form.voiceSettingsSectionTitle')}
                    </span>
                    <span className="text-xs text-content-medium">{t('form.voiceSettingsSectionDescription')}</span>
                </div>
                <div className="flex gap-4">
                    <Controller
                        name="voiceModel"
                        control={form.control}
                        render={({ field }) => (
                            <FormField
                                label={t('form.voiceModel')}
                                error={formErrors.voiceModel}
                                helpText={t('form.voiceModelHelpText')}
                                className="flex-1"
                            >
                                <SelectField
                                    {...field}
                                    options={voiceModelOptions}
                                    renderOption={(option) => option.label}
                                />
                            </FormField>
                        )}
                    />
                    <Controller
                        name="voiceSpeed"
                        control={form.control}
                        rules={{
                            validate: (value: number) =>
                                !speedAdjustable ||
                                (Number.isFinite(value) && value >= VOICE_SPEED_MIN && value <= VOICE_SPEED_MAX) ||
                                (t('form.voiceSpeedInvalidError', {
                                    min: VOICE_SPEED_MIN,
                                    max: VOICE_SPEED_MAX,
                                }) as string),
                        }}
                        render={({ field }) => (
                            <FormField
                                label={t('form.voiceSpeed')}
                                error={formErrors.voiceSpeed}
                                helpText={
                                    speedAdjustable
                                        ? t('form.voiceSpeedHelpText', { min: VOICE_SPEED_MIN, max: VOICE_SPEED_MAX })
                                        : t('form.voiceSpeedUnavailableHelpText')
                                }
                                className={speedAdjustable ? 'flex-1' : 'flex-1 opacity-50'}
                            >
                                <div className="flex gap-4 items-center">
                                    <SliderField
                                        {...field}
                                        value={speedAdjustable ? field.value : 1}
                                        disabled={!speedAdjustable}
                                        showInput={false}
                                        min={VOICE_SPEED_MIN}
                                        max={VOICE_SPEED_MAX}
                                        step={VOICE_SPEED_STEP}
                                    />
                                    <span className="flex-none min-w-[3.5rem] text-right text-sm text-content-strong tabular-nums">
                                        {Number(speedAdjustable ? field.value : 1).toFixed(2)}×
                                    </span>
                                </div>
                            </FormField>
                        )}
                    />
                </div>
            </div>

            <div className="flex flex-col gap-4 border-t border-line-weak pt-4">
                <div className="flex flex-col gap-1">
                    <span className="text-sm font-medium text-content-strong">
                        {t('form.endCallSettingsSectionTitle')}
                    </span>
                    <span className="text-xs text-content-medium">{t('form.endCallSettingsSectionDescription')}</span>
                </div>
                <Controller
                    name="maxCallDurationSeconds"
                    control={form.control}
                    rules={{
                        validate: (value: number) =>
                            (Number.isFinite(value) && value >= MAX_CALL_DURATION_MIN) ||
                            (t('form.maxCallDurationSecondsInvalidError', { min: MAX_CALL_DURATION_MIN }) as string),
                    }}
                    render={({ field }) => (
                        <FormField
                            label={t('form.maxCallDurationSeconds')}
                            error={formErrors.maxCallDurationSeconds}
                            helpText={t('form.maxCallDurationSecondsHelpText')}
                        >
                            <div className="flex gap-4 items-center">
                                <SliderField
                                    {...field}
                                    showInput={false}
                                    min={MAX_CALL_DURATION_MIN}
                                    max={MAX_CALL_DURATION_MAX}
                                    step={MAX_CALL_DURATION_STEP}
                                />
                                <span className="flex-none min-w-[4.5rem] text-right text-sm text-content-strong tabular-nums">
                                    {formatCallDuration(Number(field.value))}
                                </span>
                            </div>
                        </FormField>
                    )}
                />
            </div>

            <Controller
                name="webCallRequestsEnabled"
                control={form.control}
                render={({ field }) => (
                    <div className="flex flex-col gap-2">
                        <span className="text-sm font-medium text-content-strong">
                            {t('form.webCallRequestsEnabled')}
                        </span>
                        <span className="text-xs text-content-medium">{t('form.webCallRequestsEnabledHelpText')}</span>
                        <Switch value={field.value} onValueChange={field.onChange} />
                    </div>
                )}
            />

            {form.watch('webCallRequestsEnabled') && (
                <>
                    <Controller
                        name="webCallAskConsentQuestion"
                        control={form.control}
                        render={({ field }) => (
                            <div className="flex flex-col gap-2">
                                <span className="text-sm font-medium text-content-strong">
                                    {t('form.webCallAskConsentQuestion')}
                                </span>
                                <span className="text-xs text-content-medium">
                                    {t('form.webCallAskConsentQuestionHelpText')}
                                </span>
                                <Switch value={field.value} onValueChange={field.onChange} />
                            </div>
                        )}
                    />

                    <div className="flex gap-4">
                        <Controller
                            name="webCallAllowedPrefixes"
                            control={form.control}
                            render={({ field }) => (
                                <FormField
                                    label={t('form.webCallAllowedPrefixes')}
                                    error={formErrors.webCallAllowedPrefixes}
                                    helpText={t('form.webCallAllowedPrefixesHelpText')}
                                    className="flex-1"
                                >
                                    <StringField {...field} placeholder="+49, +43, +41" />
                                </FormField>
                            )}
                        />
                        <Controller
                            name="webCallDailyCap"
                            control={form.control}
                            rules={{
                                pattern: {
                                    value: /^[1-9]\d*$/,
                                    message: t('form.webCallDailyCapInvalidError') as string,
                                },
                            }}
                            render={({ field }) => (
                                <FormField
                                    label={t('form.webCallDailyCap')}
                                    error={formErrors.webCallDailyCap}
                                    helpText={t('form.webCallDailyCapHelpText')}
                                    className="flex-1"
                                >
                                    <StringField {...field} placeholder="50" />
                                </FormField>
                            )}
                        />
                        <Controller
                            name="webCallCooldownSeconds"
                            control={form.control}
                            rules={{
                                pattern: {
                                    value: /^\d+$/,
                                    message: t('form.webCallCooldownSecondsInvalidError') as string,
                                },
                            }}
                            render={({ field }) => (
                                <FormField
                                    label={t('form.webCallCooldownSeconds')}
                                    error={formErrors.webCallCooldownSeconds}
                                    helpText={t('form.webCallCooldownSecondsHelpText')}
                                    className="flex-1"
                                >
                                    <StringField {...field} placeholder="3600" />
                                </FormField>
                            )}
                        />
                    </div>
                </>
            )}
        </div>
    );
};

export default VoiceAgentVoiceSettingsFormFields;
